<template lang="pug">
  .b-spec-item-panel(data-testid="item-panel")
    ._bar
      ._tabs
        button._tab(type="button" data-testid="item-tab-offers" :class="{ active: tab === 'offers' }" @click="tab = 'offers'") Отклики
        button._tab(type="button" data-testid="item-tab-history" :class="{ active: tab === 'history' }" @click="openHistory") История
        ._sorts(v-if="tab === 'offers' && offers.length")
          button._sort(type="button" data-testid="offer-sort-price" @click="sortBy('price')")
            | по цене для заказчика {{ sortMark('price') }}
          button._sort(type="button" data-testid="offer-sort-total" @click="sortBy('total')")
            | по стоимости {{ sortMark('total') }}
          button._sort(type="button" data-testid="offer-sort-delivery" @click="sortBy('delivery')")
            | по сроку {{ sortMark('delivery') }}
          button._sort(type="button" data-testid="offer-sort-income" @click="sortBy('income')")
            | по доходу {{ sortMark('income') }}

      template(v-if="tab === 'offers'")
        ._empty(v-if="!offers.length" data-testid="item-offers-empty") Откликов пока нет
        table._table(v-else)
          thead
            tr
              th Поставщик
              th Состояние
              th Кол-во
              th Цена поставщика
              th Перс. скидка
              th Стоимость
              th Срок
              th Замена
              th Комментарий
              th Цена для заказчика
              th Доход дизайнера
              th
          tbody
            tr._offer(
              v-for="offer in sortedOffers"
              :key="offer.id"
              data-testid="offer-row"
              :class="{ chosen: offer.state === 'chosen', inactive: ['lost', 'withdrawn'].includes(offer.state) }"
              )
              td
                span(data-testid="offer-supplier") {{ offer.supplier && offer.supplier.name }}
                button._chat(
                  v-if="hasChat(offer)"
                  type="button"
                  data-testid="offer-chat"
                  @click="$emit('open-chat', offer)"
                  ) чат
              td
                span._state(data-testid="offer-state") {{ offerStateTitle(offer.state) }}
                span._chosen-note(v-if="offer.state === 'chosen'" data-testid="offer-chosen-note") {{ chosenNote }}
                span._changed(v-if="offer.changedAt" data-testid="offer-changed" :title="changesHint(offer)") изменён {{ dateTime(offer.changedAt) }}
              td(
                data-testid="offer-quantity"
                :class="{ 'quantity-differs': quantityDiffers(offer) }"
                :title="quantityHint(offer)"
                ) {{ quantity(offer.quantity) }}
              td(data-testid="offer-price" :class="deviationClass(offer)" :title="deviationHint(offer)") {{ money(offer.supplierPrice) }}
              td(data-testid="offer-discount") {{ discountText(offer) }}
              td(data-testid="offer-total") {{ money(offer.total) }}
              td(data-testid="offer-delivery" :class="{ exceeds: offer.deliveryExceedsRequired }") {{ deliveryText(offer.deliveryTerm, '—') }}
              //- тексты замены и комментария бывают длинными — в таблице только значок, текст в подсказке
              td(data-testid="offer-replacement")
                span._hint.replacement(v-if="offer.isReplacement" :title="offer.replacementText || 'замена'")
                  Icon(name="swap")
              td(data-testid="offer-comment")
                span._hint(v-if="offer.comment" :title="offer.comment")
                  Icon(name="notification-comment" colored)
              td(data-testid="offer-customer-price") {{ money(offer.customerPrice) }}
              td(data-testid="offer-income") {{ money(offer.designerIncome) }}
              td._actions
                Btn(v-if="canChoose(offer)" blue xs data-testid="offer-choose" @click="$emit('choose', offer)") Выбрать
                Btn(v-if="canChangeSplit(offer)" grey xs data-testid="offer-change-split" @click="$emit('change-split', offer)") Изменить разделение
                Btn(v-if="canRework(offer)" grey xs data-testid="offer-rework" @click="$emit('rework', offer)") На доработку

      ._history(v-else)
        ._empty(v-if="!history.length") Записей пока нет
        ._record(v-for="record in history" :key="record.id" data-testid="item-history-record")
          span._time {{ dateTime(record.createdAt) }}
          span._field {{ fieldTitle(record.field) }}
          span._values {{ record.oldValue || '—' }} → {{ record.newValue || '—' }}
          span._reason(v-if="record.reason") {{ record.reason }}
</template>

<script>
import specItemApi from '@/api/specItem';
import specificationApi from '@/api/specification';
import specChat from '@/model/specChat';
import {
  money, quantity, deliveryText, dateTime, percent, offerStateTitle, toNumber, deviationLevel,
  priceDeviationPercent, fieldTitle, idOf,
} from '@/utils/spec';

/**
 * Раскрытая строка позиции: вкладки «Отклики» и «История» (ТЗ 6.2). Расчётные поля откликов
 * (цена для заказчика и доход при нулевой скидке заказчику, превышение срока) приходят с
 * сервера — здесь только показ и сортировка по ним. Сгенерировано Claude.
 */
export default {
  name: 'SpecItemPanel',
  props: {
    item: { type: Object, required: true },
    specificationId: { type: Number, required: true },
    /** Архивный проект: выбор отклика, изменение разделения и доработка скрыты (ТЗ 6.1). */
    readOnly: Boolean,
  },
  data() {
    return {
      tab: 'offers',
      offers: [],
      history: [],
      /** Чат спецификации с каждым поставщиком: id поставщика → id чата (ТЗ 6.2). */
      chatBySupplier: {},
      sort: null,
      /** Направление сортировки: повторный клик по тому же заголовку его меняет (ТЗ 6.2). */
      sortDesc: false,
    };
  },
  computed: {
    /**
     * Пометка у выбранного отклика: до утверждения выбор дизайнера можно сменить, после — отклик
     * выиграл (ТЗ 2.5, доработка после сдачи).
     */
    chosenNote() {
      return this.item.status === 'customer_review' ? 'на согласовании у заказчика' : 'утверждён заказчиком';
    },
    /**
     * Что меняет список откликов: статус позиции, выбранный отклик, скидка заказчику и количество
     * факт. Смена выбора на согласовании статуса не меняет, поэтому одного статуса мало
     * (доработка после сдачи).
     */
    offersKey() {
      const { item } = this;
      return [item.status, idOf(item.chosenOffer), item.customerDiscount, item.quantityFact].join('|');
    },
    sortedOffers() {
      if (!this.sort) return [...this.offers];

      const key = {
        // срок без оценки в днях (текстом) — последним (ТЗ 2.4)
        delivery: (offer) => offer.deliveryDays ?? Number.MAX_SAFE_INTEGER,
        price: (offer) => toNumber(offer.customerPrice) ?? 0,
        total: (offer) => toNumber(offer.total) ?? 0,
        income: (offer) => toNumber(offer.designerIncome) ?? 0,
      }[this.sort];

      const sign = this.sortDesc ? -1 : 1;
      return [...this.offers].sort((a, b) => sign * (key(a) - key(b)));
    },
  },
  watch: {
    'item.id': { handler() { this.load(); } },
    offersKey: { handler() { this.load(); } },
  },
  created() {
    this.load();
    this.loadChats();
  },
  methods: {
    money,
    quantity,
    deliveryText,
    dateTime,
    offerStateTitle,
    fieldTitle,
    load() {
      specItemApi.fetchOffers(this.item.id).then((r) => { this.offers = r.items ?? []; });
      if (this.tab === 'history') this.loadHistory();
    },
    loadChats() {
      specChat.fetchDesignerChats(this.specificationId).then((chats) => {
        this.chatBySupplier = Object.fromEntries(chats
          .filter((chat) => chat.kind !== 'customer' && idOf(chat.supplier))
          .map((chat) => [idOf(chat.supplier), chat.id]));
      });
    },
    /** «чат» у поставщика отклика — открывает шторку чата на этой же странице (ТЗ 6.2). */
    hasChat(offer) {
      return !!this.chatBySupplier[idOf(offer.supplier)];
    },
    openHistory() {
      this.tab = 'history';
      this.loadHistory();
    },
    /**
     * Сортировка откликов по столбцу (ТЗ 6.2); повторный клик по тому же заголовку меняет
     * направление. По умолчанию дешевле и быстрее — сверху, по доходу — наоборот, больше сверху.
     */
    sortBy(key) {
      if (this.sort === key) {
        this.sortDesc = !this.sortDesc;
        return;
      }
      this.sort = key;
      this.sortDesc = key === 'income';
    },
    sortMark(key) {
      if (this.sort !== key) return '';
      return this.sortDesc ? '↓' : '↑';
    },
    loadHistory() {
      specificationApi.fetchHistory(this.specificationId, { item: this.item.id })
        .then((r) => { this.history = r.items ?? []; });
    },
    /**
     * Подсветка отклонения цены в отклике от плановой (ТЗ 6.2), та же шкала, что в строке.
     * По откликам сервер процент не отдаёт, поэтому здесь он считается на клиенте.
     */
    deviationClass(offer) {
      const level = deviationLevel(priceDeviationPercent(this.item.plannedPrice, offer.supplierPrice));
      return level ? `deviation-${level}` : null;
    },
    deviationHint(offer) {
      const share = priceDeviationPercent(this.item.plannedPrice, offer.supplierPrice);
      if (!deviationLevel(share)) return null;
      const plan = money(this.item.plannedPrice);
      const fact = money(offer.supplierPrice);
      return `Цена в отклике выше плановой на ${Math.round(share)} % (план ${plan}, в отклике ${fact})`;
    },
    /** Поставщик предложил не то количество, что запрошено (кол-во план позиции). */
    quantityDiffers(offer) {
      const offered = toNumber(offer.quantity);
      const planned = toNumber(this.item.quantity);
      return offered !== null && planned !== null && offered !== planned;
    },
    quantityHint(offer) {
      return this.quantityDiffers(offer) ? `Запрошено ${quantity(this.item.quantity)}` : null;
    },
    discountText(offer) {
      const rub = money(offer.unitDiscount, '');
      if (offer.discountType !== 'percent') return rub;
      return `${percent(offer.discountValue)} (${rub})`;
    },
    /**
     * Список изменений отклика «поле: было → стало» (ТЗ 6.2). Название поля — по-русски: сервер
     * отдаёт код поля, а интерфейс на русском (исправление дефекта этапа 7).
     */
    changesHint(offer) {
      return (offer.changes ?? [])
        .map((change) => `${fieldTitle(change.field)}: ${change.oldValue ?? '—'} → ${change.newValue ?? '—'}`)
        .join('\n');
    },
    /** «Выбрать» — у откликов «Новый» в статусах «Есть отклики» и «Отклонено заказчиком» (ТЗ 6.2). */
    /** «Выбрать» — и на согласовании у заказчика: выбор можно сменить до утверждения (ТЗ 2.5). */
    canChoose(offer) {
      return !this.readOnly && offer.state === 'new'
        && ['has_offers', 'customer_rejected', 'customer_review'].includes(this.item.status);
    },
    /**
     * «Изменить разделение» — у выбранного отклика до утверждения заказчиком (ТЗ 6.2); без
     * персональной скидки делить нечего, кнопки нет (У48).
     */
    canChangeSplit(offer) {
      return !this.readOnly && offer.state === 'chosen' && this.item.status === 'customer_review'
        && (toNumber(offer.unitDiscount) ?? 0) > 0;
    },
    /** «На доработку» по запросу этого отклика — из статуса «Есть отклики» (ТЗ 2.5). */
    canRework(offer) {
      return !this.readOnly && offer.state === 'new' && this.item.status === 'has_offers';
    },
  },
};
</script>

<style scoped lang="scss">
.spec-item-panel {
  background: #F8F9FC;
  border-bottom: 1px solid var(--color-super-light);
  font-size: 12px;

  // панель живёт внутри таблицы шириной в несколько экранов: прилипает не сама панель (она во всю
  // ширину таблицы, и прилипание на ней ничего не даёт), а полоса с её содержимым
  &__bar {
    padding: 12px 16px 16px;
    width: max-content;
    max-width: 100%;
    position: sticky;
    left: 0;
  }

  &__tabs {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 10px;
  }

  &__tab {
    border: none;
    background: none;
    font: inherit;
    font-size: 14px;
    font-weight: 700;
    color: var(--color-light-dark);
    cursor: pointer;
    padding: 0;

    &.active {
      color: var(--color-dark-gray);
    }
  }

  &__sorts {
    display: flex;
    gap: 10px;
    margin-left: auto;
  }

  &__sort {
    border: none;
    background: none;
    font: inherit;
    color: var(--color-accent);
    cursor: pointer;
    padding: 0;
  }

  &__empty {
    color: var(--color-middle-gray);
  }

  &__table {
    width: auto;
    min-width: 900px;
    border-collapse: collapse;

    th,
    td {
      text-align: left;
      padding: 5px 8px;
      border-bottom: 1px solid var(--color-super-light);
      vertical-align: top;
    }

    th {
      color: var(--color-middle-gray);
      font-weight: 400;
      white-space: nowrap;
    }
  }

  &__chat {
    margin-left: 6px;
    color: var(--color-accent);
    border: none;
    background: none;
    font: inherit;
    padding: 0;
    cursor: pointer;
    text-decoration: underline;
  }

  &__hint {
    display: inline-flex;
    color: var(--color-accent);
    cursor: help;

    &.replacement {
      color: var(--color-orange);
    }

    svg {
      width: 16px;
      height: 16px;
    }
  }

  &__changed {
    display: block;
    color: var(--color-orange);
  }

  &__actions {
    white-space: nowrap;

    > * + * {
      margin-left: 6px;
    }
  }

  &__record {
    display: flex;
    gap: 12px;
    padding: 4px 0;
    border-bottom: 1px solid var(--color-super-light);
  }

  &__time {
    color: var(--color-middle-gray);
    white-space: nowrap;
  }
}

.exceeds {
  color: var(--color-red);
}

// выбранный отклик виден сразу: зелёная строка с полосой слева и пометкой, на каком он этапе;
// проигравшие и отозванные приглушены
.spec-item-panel__offer.chosen td {
  background: #EAF7EE;
}

.spec-item-panel__offer.chosen td:first-child {
  box-shadow: inset 3px 0 0 #2E9E5B;
}

.spec-item-panel__offer.chosen .spec-item-panel__state {
  font-weight: 700;
  color: #2E9E5B;
}

.spec-item-panel__chosen-note {
  display: block;
  font-size: 11px;
  color: #2E9E5B;
}

.spec-item-panel__offer.inactive td {
  color: var(--color-middle-gray);
}

.quantity-differs {
  color: var(--color-orange);
  font-weight: 700;
}

.deviation-medium { background: #FFF6DC; }
.deviation-high { background: #FFE8E8; }
</style>
